import { useRef, useState, useCallback } from "react";
import AgentStream from "../API/AgentStream";

/**
 * Custom hook for managing SSE streaming from the agent endpoint
 * @returns {object} - Hook state and methods
 */
const useAgentStream = () => {
  const abortControllerRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const accumulatedTextRef = useRef("");

  /**
   * Start streaming from the agent endpoint
   * @param {object} params - Streaming parameters
   * @param {string} params.chatid - Chat ID
   * @param {string} params.workspaceid - Workspace ID
   * @param {string} params.token - API token
   * @param {Array} params.messages - Messages array
   * @param {string} params.agentid - Agent ID
   * @param {string} [params.baseImage] - Base image for vision
   * @param {string} [params.canvasSelection] - Canvas selection data
   * @param {string} [params.methodName] - Endpoint method name ('agent' or 'planner'), defaults to 'agent'
   * @param {string} [params.nextMessageId] - Next message ID for planner mode
   * @param {string} [params.boundFolderPath] - Bound folder path for local folder access tool
   * @param {object} callbacks - Callback functions
   * @param {function} callbacks.onToken - Called for each text token
   * @param {function} callbacks.onMessage - Called for status/metadata updates
   * @param {function} callbacks.onComplete - Called when stream completes
   * @param {function} callbacks.onError - Called on error
   */
  const startStream = useCallback(async (params, callbacks) => {
    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    accumulatedTextRef.current = "";
    setIsStreaming(true);
    setConnectionStatus("connecting");

    try {
      await AgentStream.streamAgentResponse({
        ...params,
        signal: abortControllerRef.current.signal,
        onToken: (chunk, messageId) => {
          accumulatedTextRef.current += chunk;
          callbacks.onToken?.(chunk, messageId, accumulatedTextRef.current);
        },
        onMessage: (data) => {
          if (data.type === "connection" && data.status === "connected") {
            setConnectionStatus("connected");
          }
          callbacks.onMessage?.(data);
        },
        onError: (error) => {
          setIsStreaming(false);
          setConnectionStatus("disconnected");
          callbacks.onError?.(error);
        },
        onComplete: () => {
          setIsStreaming(false);
          setConnectionStatus("disconnected");
          callbacks.onComplete?.(accumulatedTextRef.current);
        },
      });
    } catch (error) {
      setIsStreaming(false);
      setConnectionStatus("disconnected");
      callbacks.onError?.(error);
    }
  }, []);

  /**
   * Cancel the current stream
   */
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setConnectionStatus("disconnected");
  }, []);

  /**
   * Get the accumulated text so far
   */
  const getAccumulatedText = useCallback(() => {
    return accumulatedTextRef.current;
  }, []);

  /**
   * Reset the accumulated text (call on message_sealed to prevent
   * text from previous message segments bleeding into continuation messages)
   */
  const resetAccumulatedText = useCallback(() => {
    accumulatedTextRef.current = "";
  }, []);

  return {
    startStream,
    cancelStream,
    isStreaming,
    connectionStatus,
    getAccumulatedText,
    resetAccumulatedText,
  };
};

export default useAgentStream;
